import { useEffect, useState } from 'react';

type ObserveFunction = (value: string) => void;

// Keep all hooks refering to the same key in local storage synchronized
class LocalStorageObservers {
  private observers: { [key: string]: ObserveFunction[] } = {};

  public addObserver(key: string, callback: ObserveFunction) {
    this.observers[key] = this.observers[key] ?? [];
    this.observers[key].push(callback);
  }

  public removeObserver(key: string, callback: ObserveFunction) {
    this.observers[key] = this.observers[key].filter((observer: any) => observer !== callback);
  }

  public notifyObservers(key: string, value: string) {
    this.observers[key].map(fn => fn(value));
  }
}

const localStorageObservers = new LocalStorageObservers();

export const useLocalStorage = (key: string, initialValue: string = ''): any => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage?.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (e) {
      console.error(e);
    }
  });

  const setValue = (value: string) => {
    try {
      window.localStorage?.setItem(key, JSON.stringify(value));
      setStoredValue(value);
      localStorageObservers.notifyObservers(key, value);
    } catch (e) {
      console.error(e);
    }
  };

  const removeValue = (value: string = '') => {
    try {
      window.localStorage?.removeItem(key);
      setStoredValue(value);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    localStorageObservers.addObserver(key, setStoredValue);
    return () => localStorageObservers.removeObserver(key, setStoredValue);
  }, [key]);

  return [storedValue, setValue, removeValue];
};
